import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  combineLatestWith,
  combineLatest,
  iif,
  map,
  Observable,
  tap,
  filter,
} from "rxjs";
import { DailyScoreRecord, Stats } from "@model";
import {
  DailyScoreService,
  ScoreHttpService,
  DEFAULT_DAILY_POINTS,
  UserService,
} from "@services";
import {
  getTodaysDay,
  isNotNullOrUndefined,
  generateStats,
  fillMissingRecordsData,
} from "@helpers";
import { CurrentUserService } from "@core";

const MAX_STATS_LENGTH = 100;
const MIN_STATS_LENGTH = 30;

@Injectable({ providedIn: "root" })
export class ScoreStatsService {
  private _dayWhenGotPastResult = 0;
  private _pastResults$ = new BehaviorSubject<DailyScoreRecord[]>([]);
  private _userDayJoined$ = new BehaviorSubject<number>(0);

  stats$: Observable<Stats>;

  _allResults$: Observable<DailyScoreRecord[]>;

  constructor(
    private _userService: UserService,
    private _currentUserService: CurrentUserService,
    private _dailyScoreService: DailyScoreService,
    private _scoreHttpService: ScoreHttpService,
  ) {
    this._userService.user$.subscribe((user) => {
      this._userDayJoined$.next(user ? user.dayJoined : 0);
    });

    this._currentUserService.currentUser$.subscribe((user) => {
      if (!user) {
        this._dayWhenGotPastResult = 0;
      }
    });

    this._allResults$ = iif(
      () => getTodaysDay() === this._dayWhenGotPastResult,
      this._pastResults$,
      this._getPastResults$(),
    ).pipe(
      combineLatestWith(this._dailyScoreService.currentDailyScore$.pipe(isNotNullOrUndefined())),
      map(([pastDays, currentDay]) => {
        return [...pastDays, currentDay];
      }),
      tap(() => {
        this._dayWhenGotPastResult = getTodaysDay();
      }),
    );

    this.stats$ = combineLatest([this._allResults$, this._userDayJoined$]).pipe(
      filter(([, dayJoined]) => !!dayJoined),
      map(([results, dayJoined]) => generateStats(results, dayJoined)),
    );
  }

  private _getPastResults$(): Observable<DailyScoreRecord[]> {
    const today = getTodaysDay();
    const yesterdayDay = today - 1;
    const pastHundredDay = yesterdayDay - MAX_STATS_LENGTH + 2;

    return this._scoreHttpService.getDailyScoreForDateRange(pastHundredDay, yesterdayDay).pipe(
      map((results: DailyScoreRecord[]) => {
        const days = results.map((r) => r.dailyScoreDay);
        const minDayResult = Math.min(...days);

        const startDay =
          today - minDayResult < MIN_STATS_LENGTH + 1
            ? today - MIN_STATS_LENGTH + 1
            : minDayResult;

        return fillMissingRecordsData(results, startDay, yesterdayDay, DEFAULT_DAILY_POINTS);
      }),
      tap((result) => {
        this._pastResults$.next(result);
      }),
    );
  }
}
